import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { useMutation } from 'react-query';
import {
  Box,
  TextInput,
  Checkbox,
  ArrowLeftIcon,
  Modal,
  ModalBody,
  DatePicker,
  Button,
  useToast,
} from '@razorpay/blade/components';
import api from 'api';
import { useForm } from 'forms';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { AppError } from 'utils/AppError';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { getValidationSchema } from './utils';
import { FormFields } from './types';
import { SelectManager } from '../components/SelectManager';
import { CREATE_CONTRACTOR, initialFormData } from './data';
import { SuccessModal } from './components/SuccessModal';
import { DiscardModal } from './components/DiscardModal';
import { FloatingDiscardButton } from '../components/FloatingDiscardButton';
import {
  FormHeading,
  StyledForm,
  FormFooterWrapper,
  MainContentWrapper,
  MultiStepFormWrapper,
  FormFieldsWrapper,
  BorderedBox,
} from '../components/styles';
import { getPeoplePage } from 'utils/links';
import useFeature from 'hooks/useFeature';
import { PayrollCreateContractorInput } from 'api/gql-utils/employeeOnboardingGql/types';
import { VERIFY_PAN } from 'api/employeeOnboarding';
import { getDuplicateDetails, getGeneratedEmail } from '../utils';
import { dateFormats } from 'utils/Dates';
import DuplicateChecksModal from 'components/EmployeeDetails/StatutoryContributions/components/DuplicateChecksModal';
import { getContractorText, isContractorsPanValidationEnabled } from 'utils/getContractorText';
import useReduxSelector from 'utils/useReduxSelector';

export const CreateContractor = (): React.ReactElement => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [isDuplicateChecksModalOpen, setIsDuplicateChecksModalOpen] = useState(false);
  const toast = useToast();
  const orgId = useSelector(loggedInUserSelectors.organizationID);
  const history = useHistory();
  const { isEmployeeOnboardingEnabled } = useFeature('employeeOnboarding');
  const isRxCurrentAccountMerchant = !!useReduxSelector(loggedInUserSelectors.currentOrganization)
    ?.isRxCurrentAccountMerchant;
  const featureFlags = useReduxSelector(loggedInUserSelectors.features);
  const isPersonalPanValidationEnabled = isContractorsPanValidationEnabled({
    isRxCurrentAccountMerchant,
    featureFlags,
  });

  const { contractorTitleCase, contractor } = getContractorText({
    isRxCurrentAccountMerchant,
    featureFlags,
  });

  const { register, validate, formData, setIsSubmitting, isSubmitting, setFieldError } =
    useForm<FormFields>(initialFormData);

  const getPayload = (formData: NonNullable<FormFields>): PayrollCreateContractorInput => {
    const generatedEmail = getGeneratedEmail(orgId);

    return {
      name: formData.name,
      emailId: formData.email_not_present ? generatedEmail : formData.email_id,
      hireDate: formData.hire_date ? format(formData.hire_date, dateFormats.yearMonthDate) : '',
      designation: formData.designation,
      department: formData.department || '',
      managerPeopleId: formData.manager_people_id ? Number(formData.manager_people_id) : undefined,
      pan: formData.pan,
      emailNotPresent: Boolean(formData.email_not_present),
    };
  };

  const mutateCreateContractor = useMutation({
    mutationKey: CREATE_CONTRACTOR,
    mutationFn: api.employeeOnboardingGql.mutations.createContractor,
    onSuccess: (response) => {
      if (response.payrollCreateContractor.data.inviteSent) {
        setShowSuccessModal(true);
        toast.show({
          content: `An invite sent to ${formData.name} to enter their information`,
          color: 'positive',
        });
      } else {
        toast.show({
          content: `${contractorTitleCase} profile created for ${formData.name}`,
          color: 'positive',
        });
        history.push(`/employeeDetail?userId=${response.payrollCreateContractor.data.peopleId}`);
      }
      setIsSubmitting(false);
    },
    onError: (error: typeof AppError) => {
      const duplicateDetails = getDuplicateDetails(error);
      if (duplicateDetails) {
        setIsDuplicateChecksModalOpen(true);
      }
      toast.show({
        content: parseGraphqlErrorFromExtensions(error),
        color: 'negative',
      });
      setIsSubmitting(false);
    },
  });

  const mutatePanVerification = useMutation({
    mutationKey: VERIFY_PAN,
    mutationFn: api.employeeOnboarding.verifyPan,

    onSuccess: (response) => {
      if (response.data.validation_status === 'valid') {
        if (formData.name && formData.hire_date && formData.designation && formData.pan) {
          const payload = getPayload(formData as NonNullable<FormFields>);
          mutateCreateContractor.mutate(payload);
        }
      } else {
        setFieldError('pan', response.data.error);
        toast.show({
          content: 'Invalid PAN details',
          color: 'negative',
        });
      }
      setIsSubmitting(false);
    },
    onError: (error: typeof AppError) => {
      setFieldError('pan', parseGraphqlErrorFromExtensions(error));
      toast.show({
        content: parseGraphqlErrorFromExtensions(error),
        color: 'negative',
      });
      setIsSubmitting(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate(getValidationSchema(isPersonalPanValidationEnabled))) {
      return;
    }
    setIsSubmitting(true);
    if (mutatePanVerification.data?.data.validation_status === 'valid') {
      const payload = getPayload(formData as NonNullable<FormFields>);
      mutateCreateContractor.mutate(payload);
      return;
    }

    if (formData.pan && formData.name) {
      mutatePanVerification.mutate({
        pan: formData.pan,
        name: formData.name,
        employee_type: 'contractor',
      });
    }
  };

  const handleModalClose = () => {
    const peoplePageUrl = getPeoplePage(isEmployeeOnboardingEnabled);
    window.location.href = peoplePageUrl;
  };

  const handleDiscardModal = () => {
    setShowDiscardModal(true);
  };

  const handleDiscardModalCancel = () => {
    setShowDiscardModal(false);
  };

  const handleDiscardModalConfirm = () => {
    setShowDiscardModal(false);
    handleModalClose();
  };

  const handleSuccessModalContinue = () => {
    setShowSuccessModal(false);
    handleModalClose();
  };

  const duplicateDetails = getDuplicateDetails(mutateCreateContractor.error);

  return (
    <Box>
      <Modal
        isOpen={true}
        onDismiss={handleModalClose}
        size="full"
        accessibilityLabel="Add Contractor">
        <ModalBody padding="spacing.0">
          <MultiStepFormWrapper>
            <MainContentWrapper marginLeft="spacing.0">
              <FloatingDiscardButton handleDiscardModal={handleDiscardModal} />

              <StyledForm onSubmit={handleSubmit}>
                <FormFieldsWrapper>
                  <FormHeading
                    title={`Add ${contractorTitleCase}`}
                    description={`Enter the ${contractor}'s basic information to set up their profile`}
                  />

                  <BorderedBox>
                    <TextInput
                      autoFocus
                      {...register('name')}
                      label="Name"
                      placeholder="Eg. John Doe"
                      helpText={`The ${contractor}'s full legal name as it appears on their government ID`}
                      size="medium"
                      necessityIndicator="required"
                      isRequired
                    />

                    <TextInput
                      {...register('email_id')}
                      label="Email ID"
                      placeholder="Eg. john.doe@corporation.com"
                      size="medium"
                      necessityIndicator={!formData.email_not_present ? 'required' : 'none'}
                      isDisabled={formData.email_not_present}
                      type="email"
                    />

                    <Checkbox {...register('email_not_present', 'checkbox')}>
                      {contractorTitleCase} does not have an email address
                    </Checkbox>

                    <Box position="relative">
                      <DatePicker
                        {...register('hire_date', 'date')}
                        selectionType="single"
                        label="Hire Date"
                        necessityIndicator="required"
                        isRequired
                        size="medium"
                        helpText={`Your ${contractor}'s first day of work at your company`}
                      />
                    </Box>

                    <TextInput
                      {...register('designation')}
                      label="Designation"
                      placeholder="Eg. Backend Developer"
                      size="medium"
                      necessityIndicator="required"
                      isRequired
                    />

                    <TextInput
                      {...register('department')}
                      label="Department"
                      placeholder="Eg. IT"
                      size="medium"
                    />

                    <SelectManager formData={formData} registerFormData={register} />

                    <TextInput
                      {...register('pan')}
                      label="PAN"
                      placeholder="Eg: ABCPE1234F"
                      helpText={`${contractorTitleCase}'s PAN number is required to calculate their income tax.`}
                      size="medium"
                      autoCapitalize="characters"
                      necessityIndicator="required"
                      successText={
                        mutatePanVerification.data?.data.validation_status === 'valid'
                          ? 'PAN verified'
                          : undefined
                      }
                      isRequired
                      validationState={
                        mutatePanVerification.data?.data.validation_status === 'valid'
                          ? 'success'
                          : register('pan').validationState
                      }
                    />
                  </BorderedBox>
                </FormFieldsWrapper>
                <FormFooterWrapper>
                  <Button
                    variant="tertiary"
                    size="medium"
                    type="button"
                    icon={ArrowLeftIcon}
                    onClick={handleModalClose}
                    isDisabled={isSubmitting}>
                    Back
                  </Button>
                  <Button variant="primary" size="medium" type="submit" isLoading={isSubmitting}>
                    {formData.email_not_present ? 'Continue' : 'Send Invite'}
                  </Button>
                </FormFooterWrapper>
              </StyledForm>
            </MainContentWrapper>
          </MultiStepFormWrapper>
        </ModalBody>
      </Modal>

      <SuccessModal
        isOpen={showSuccessModal}
        onDismiss={handleSuccessModalContinue}
        employeeName={formData.name || ''}
        userId={mutateCreateContractor.data?.payrollCreateContractor.data.peopleId}
      />

      <DiscardModal
        isOpen={showDiscardModal}
        onCancel={handleDiscardModalCancel}
        onDiscard={handleDiscardModalConfirm}
      />

      <DuplicateChecksModal
        isOpen={isDuplicateChecksModalOpen}
        onDismiss={() => setIsDuplicateChecksModalOpen(false)}
        details={duplicateDetails}
      />
    </Box>
  );
};
